import React, { useState } from "react";
import { Button } from "primereact/button";
import type { ThgAnnualSummary } from "../../../types/thgAnnualSummary";
import { downloadCsv } from "../../StationPerformance/utils/csv";
import { promptFileDownload } from "../../../utils/fileDownload";
import { buildThgCsvRows, THG_CSV_OPTIONS, thgCsvFilename } from "../utils/thgCsv";
import { downloadThgXlsx } from "../utils/thgXlsx";

interface ThgExportButtonProps {
  summary: ThgAnnualSummary;
  /** Reports a failed Excel export, which builds the workbook asynchronously. */
  onError?: (message: string) => void;
}

/**
 * Exports the year in finance's payout-sheet format. The formatted Excel file is the main action, since
 * finance opens it in Google Sheets and a CSV keeps none of its widths, colours or number formats; the
 * CSV stays for anyone loading the figures into another tool. Neither is labelled "for payout": a
 * provisional list must never be offered that way, and both files say PROVISIONAL in their name and rows.
 */
const ThgExportButton: React.FC<ThgExportButtonProps> = ({ summary, onError }) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportXlsx = async () => {
    setIsExporting(true);
    try {
      await downloadThgXlsx(summary, promptFileDownload);
    } catch (error) {
      console.error("Failed to build the THG payout workbook:", error);
      onError?.("Could not create the Excel file. Please try again, or export the CSV instead.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Button
        label="Export Excel"
        icon="pi pi-file-excel"
        outlined
        size="small"
        loading={isExporting}
        disabled={isExporting}
        onClick={handleExportXlsx}
      />
      <Button
        label="Export CSV"
        icon="pi pi-download"
        text
        size="small"
        onClick={() => downloadCsv(thgCsvFilename(summary), buildThgCsvRows(summary), THG_CSV_OPTIONS)}
      />
    </>
  );
};

export default ThgExportButton;
